import clsx from "clsx";
import { ChevronRight, RotateCcw, Workflow, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../../api/client";
import type { DeployedUnit, Phase, PropagationChange } from "../../api/types";
import { motion, useLastDefined, usePresence } from "../../components/presence";
import { Badge, Button, IconButton } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { unitDesignation } from "../../i18n/designation";
import { t } from "../../i18n/i18n";
import { unitMissionName } from "../../i18n/vocabulary";
import { useUi } from "../../state/ui";
import { dossierWrite, recordWrite, writeFailure } from "../dossierWrites";
import { useManoeuvreWrites } from "../manoeuvre/useManoeuvreWrites";
import {
  causeText,
  defensiveRoles,
  changeText,
  changeWhy,
  headline,
  hintText,
  postureChange,
  restoreItems,
  roleShort,
  usePropagation,
} from "./propagation";
import { useDeployment } from "./useDeployment";

/** Durée d'affichage de l'avis (plus longue que celle des avis ordinaires : il se lit ligne à ligne), suspendue au
 *  survol et au focus. */
const DURATION_MS = 20_000;
/** Lignes visibles avant défilement. */
const MAX_LINES_HEIGHT = 232;

/** Avis récapitulatif de la propagation des missions (docs/25 §6) : cause → effets, posture avant / après, une
 *  ligne par unité (rôle, nouvelles missions, « pourquoi » en une phrase), chaque ligne centrant la carte sur son
 *  unité et s'annulant seule ; « Annuler tout » défait l'écriture (comme ⌘Z). Action attendue quand le choix ne
 *  suffit pas à changer la posture : les autres unités de 1er échelon à aligner, désignées. Apparition fluide depuis
 *  la barre basse, comme les avis. */
export function PropagationCard() {
  const current = usePropagation((s) => s.entry);
  const close = usePropagation((s) => s.close);
  const undone = usePropagation((s) => s.undone);
  const markUndone = usePropagation((s) => s.markUndone);
  const spotlight = usePropagation((s) => s.spotlight);
  const { mounted, closing } = usePresence(Boolean(current));
  const entry = useLastDefined(current);
  const { data: deployment } = useDeployment();
  const phase = useUi((s) => s.phase);
  const select = useUi((s) => s.select);
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const setPhase = useUi((s) => s.setPhase);
  const queryClient = useQueryClient();
  const { setMission } = useManoeuvreWrites();
  const [paused, setPaused] = useState(false);
  const [aligning, setAligning] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(null);
  }, [current?.id]);

  useEffect(() => {
    if (!current || paused || aligning) return;
    const timer = window.setTimeout(close, DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [current, paused, aligning, close]);

  const restore = useMutation({
    mutationFn: (change: PropagationChange) =>
      dossierWrite(queryClient, (target) => api.restoreMissions(target, restoreItems(change)), {
        notSaved: t("Enregistrez le dossier pour changer la mission."),
        channel: "dispositif",
      }),
    onSuccess: (write, change) => {
      recordWrite(write, t("mission de {unit} rétablie", { unit: unitDesignation(change.designation) }));
      markUndone(change.unit);
    },
    onError: (error) => writeFailure(queryClient, error, t("Mission non enregistrée")),
  });

  if (!mounted || !entry) return null;
  const { propagation } = entry;
  const posture = postureChange(propagation);
  const hint = hintText(propagation);
  const units = new Map<string, DeployedUnit>((deployment?.units ?? []).map((u) => [u.key, u]));

  const locate = (change: PropagationChange) => {
    const unit = units.get(change.unit);
    const at: Phase = change.missions.some((m) => m.phase === phase) || !change.missions.length ? phase : change.missions[0]!.phase;
    const pos = unit?.positions[at];
    if (!unit || !pos) return;
    if (at !== phase) setPhase(at);
    select({ layer: "unit", code: unit.code, lngLat: [pos.lon, pos.lat] });
    requestFlyTo([pos.lon, pos.lat], 9.5);
    spotlight(change.unit);
    setOpen(change.unit);
  };

  const alignOthers = async () => {
    const h = propagation.hint;
    if (!h?.mission || aligning) return;
    setAligning(true);
    // Une écriture à la fois : la suivante part une fois la précédente enregistrée (version du dossier). L'avis reste
    // affiché pendant l'écriture (il porte la mutation) ; la réponse ouvre l'avis de la propagation qui suit.
    try {
      for (const other of h.others) {
        await setMission.mutateAsync({ unit: other.unit, phase: h.phase, mission: h.mission, reposition: false });
      }
    } catch {
      // Refus déjà signalé par l'écriture (avis d'échec).
    } finally {
      setAligning(false);
    }
  };

  return (
    <div
      ref={ref}
      key={entry.id}
      role="status"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!ref.current?.contains(e.relatedTarget as Node)) setPaused(false);
      }}
      className={clsx(
        "pointer-events-auto mb-2 flex w-[min(560px,calc(100vw-1.5rem))] flex-col rounded-xl border border-line bg-surface shadow-float",
        motion("bottom", closing),
      )}
    >
      <div className="flex items-start gap-3 py-2.5 pr-2 pl-3.5">
        <Workflow size={16} className="mt-0.5 shrink-0 text-accent" aria-hidden />
        <div className="min-w-0 flex-1 py-px">
          <div className="text-ui leading-snug font-semibold break-words text-ink">{headline(propagation)}</div>
          <div className="mt-0.5 text-meta leading-snug break-words text-ink-muted">{causeText(propagation)}</div>
          {posture && <div className="mt-0.5 text-meta leading-snug font-medium break-words text-ink">{posture}</div>}
        </div>
        <IconButton size="sm" onClick={close} title={t("Fermer")} aria-label={t("Fermer")} className="-my-1">
          <X size={14} />
        </IconButton>
      </div>

      {propagation.changes.length > 0 && (
        <ul className="overflow-y-auto border-t border-line px-1.5 py-1.5" style={{ maxHeight: MAX_LINES_HEIGHT }}>
          {propagation.changes.map((change) => {
            const why = changeWhy(change);
            const done = undone.includes(change.unit);
            const role = roleShort(change.role_after, change.side, defensiveRoles(deployment));
            const expanded = open === change.unit;
            return (
              <li key={change.unit} className="flex items-start gap-1">
                <button
                  type="button"
                  onClick={() => locate(change)}
                  title={t("Centrer la carte sur {unit}", { unit: unitDesignation(change.designation) })}
                  className={clsx(
                    "focus-inset flex min-h-8 min-w-0 flex-1 items-start gap-2 rounded-lg py-1.5 pr-1.5 pl-2 text-left transition-colors hover:bg-hover",
                    expanded && "bg-selected",
                    done && "opacity-50",
                  )}
                >
                  <span
                    aria-hidden
                    className={clsx(
                      "mt-1.5 size-2 shrink-0 rounded-full",
                      change.side === "ENI" ? "bg-hostile" : "bg-friend",
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-ui text-ink">
                      <span className="font-semibold">{unitDesignation(change.designation)}</span>
                      {role && (
                        <Badge size="sm" tone={change.side === "ENI" ? "hostile" : "friend"}>
                          {role}
                        </Badge>
                      )}
                      <span className="min-w-0 break-words">{changeText(change, defensiveRoles(deployment))}</span>
                      {done && <span className="text-meta text-ink-muted">{t("rétablie")}</span>}
                    </span>
                    {why && (
                      <span className={clsx("block text-meta leading-snug text-ink-muted", !expanded && "truncate")}>
                        {why}
                      </span>
                    )}
                  </span>
                  <ChevronRight size={14} className="mt-1 shrink-0 text-ink-soft" aria-hidden />
                </button>
                {change.missions.length > 0 && !done && (
                  <IconButton
                    size="sm"
                    className="mt-1"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(change)}
                    title={t("Rétablir les missions d'avant pour {unit} : {missions}", {
                      unit: unitDesignation(change.designation),
                      missions: change.missions.map((m) => `${m.phase} ${m.before ? unitMissionName(m.before) : t("Sans mission")}`).join(", "),
                    })}
                    aria-label={t("Rétablir les missions d'avant pour {unit}", { unit: unitDesignation(change.designation) })}
                  >
                    <RotateCcw size={14} />
                  </IconButton>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hint && (
        <div className="border-t border-line px-3.5 py-2.5 text-meta leading-snug text-ink">
          {hint}
        </div>
      )}

      {(entry.detail || entry.undo || entry.order || hint) && (
        <div className="border-t border-line py-2 pr-2 pl-3.5">
          {entry.detail && <p className="pr-1.5 text-meta leading-snug text-ink-muted">{entry.detail}</p>}
          <div className={clsx("flex flex-wrap items-center justify-end gap-x-1 gap-y-1", entry.detail && "mt-1.5")}>
            {hint && propagation.hint?.mission && (
              <Button variant="secondary" size="sm" className="mr-auto" highlight={!aligning} disabled={aligning} onClick={() => void alignOthers()}>
                {aligning ? (
                  <Waiting>{t("Enregistrement…")}</Waiting>
                ) : propagation.hint.others.length === 1 ? (
                  t("Choisir {mission} pour {unit}", {
                    mission: unitMissionName(propagation.hint.mission),
                    unit: unitDesignation(propagation.hint.others[0]!.designation),
                  })
                ) : (
                  t("Choisir {mission} pour {n} autres", {
                    mission: unitMissionName(propagation.hint.mission),
                    n: propagation.hint.others.length,
                  })
                )}
              </Button>
            )}
            {entry.undo && (
              <Button
                variant="link"
                className="px-2 py-1"
                onClick={() => {
                  // L'écriture et les lignes déjà rétablies une à une : un seul groupe d'annulations (comme ⌘Z répété).
                  for (let i = 0; i <= undone.length; i++) entry.undo?.();
                  close();
                }}
              >
                {t("Annuler tout")}
              </Button>
            )}
            {entry.order && (
              <Button
                variant="link"
                className="px-2 py-1"
                onClick={() => {
                  entry.order?.run();
                  close();
                }}
              >
                {entry.order.label}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
